class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.initClearCartLink();
    this.handleBrowserBackForward();
  }

  handleBrowserBackForward() {
    // Handle browser back/forward cache (bfcache) restoration
    window.addEventListener('pageshow', (event) => {
      console.log('pageshow event fired, persisted:', event.persisted);
      if (event.persisted) {
        // Page was restored from bfcache, refresh cart drawer contents
        console.log('Page restored from bfcache, refreshing cart drawer');
        this.refreshCartDrawerFromCache();
      }
    });

    // Also check on DOMContentLoaded in case pageshow doesn't catch it
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.checkAndRefreshCart();
      });
    } else {
      // DOM already loaded, check immediately
      this.checkAndRefreshCart();
    }
  }

  checkAndRefreshCart() {
    // Check if cart icon shows items but drawer might be stale
    const cartIconBubble = document.getElementById('cart-icon-bubble');
    if (!cartIconBubble) return;

    const countElement = cartIconBubble.querySelector('.cart-count-bubble span');
    if (!countElement) return;

    const iconCount = parseInt(countElement.textContent) || 0;

    // If icon shows items, verify drawer is in sync
    if (iconCount > 0) {
      const cartItems = this.querySelectorAll('.cart-item, .cart-card');
      console.log(`Cart icon shows ${iconCount} items, drawer has ${cartItems.length} items in DOM`);

      if (cartItems.length === 0 && this.classList.contains('is-empty')) {
        console.log('Cart drawer out of sync, forcing refresh');
        this.refreshCartDrawerFromCache();
      }
    }
  }

  refreshCartDrawerFromCache() {
    const cartDrawerItems = document.querySelector('cart-drawer-items');
    if (cartDrawerItems) {
      cartDrawerItems.onCartUpdate('bfcache-restore');
    }
  }

  initClearCartLink() {
    // Use event delegation to handle clear cart link clicks
    document.addEventListener('click', (event) => {
      if (event.target.id === 'clear-cart-link' || event.target.closest('#clear-cart-link')) {
        event.preventDefault();
        this.clearCart();
      }
    });
  }

  clearCart() {
    if (!confirm('Are you sure you want to clear your cart?')) {
      return;
    }

    // Show loading
    this.showSimpleLoading();

    // Clear all items by setting quantity to 0 for each
    fetch('/cart/clear.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => response.json())
      .then((cart) => {
        console.log('Cart cleared successfully');

        // Trigger cart drawer update - it will handle hiding the loading overlay
        const cartDrawerItems = document.querySelector('cart-drawer-items');
        if (cartDrawerItems) {
          cartDrawerItems.onCartUpdate();
        }

        // Update header cart icon
        if (window.updateCartIcon) {
          window.updateCartIcon(cart);
        }

        // Dispatch cart updated event
        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: { source: 'clear-cart', cartData: cart },
          })
        );
      })
      .catch((error) => {
        console.error('Failed to clear cart:', error);
        alert('Failed to clear cart. Please try again.');
        // Only hide loading on error
        this.hideSimpleLoading();
      });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // Don't show loading when just reopening the drawer
    // Loading will only be shown when cart actually changes (via onCartUpdate)

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    document.body.classList.add('overflow-hidden');

    // Initialize price stages when drawer opens
    if (window.initializeCartPriceStages) {
      setTimeout(() => {
        window.initializeCartPriceStages();
      }, 100);
    }

    // Dispatch custom event for other scripts to listen to
    document.dispatchEvent(new CustomEvent('cart-drawer:open'));

    // Preload recommendations immediately when drawer opens
    this.preloadRecommendations();
  }

  preloadRecommendations() {
    // Check if complementary recommendations are enabled
    const miniCollection = this.querySelector('.cart-drawer__mini-collection');
    if (!miniCollection) return;

    const recommendationType = miniCollection.getAttribute('data-recommendation-type');
    if (recommendationType !== 'cart_aware_recommendations') return;

    // Check if recommendations are already loaded
    const productsContainer = miniCollection.querySelector('.cart-drawer__mini-collection-products');
    const hasExistingProducts =
      productsContainer && productsContainer.querySelector('.cart-drawer__mini-collection-item');

    if (hasExistingProducts) {
      console.log('Recommendations already loaded, skipping preload on drawer reopen');
      return;
    }

    // Trigger new complementary recommendations system
    console.log('🔄 Loading complementary recommendations on drawer open');

    setTimeout(() => {
      if (window.cartComplementaryRecs) {
        window.cartComplementaryRecs.loadRecommendations();
      }
    }, 100);
  }

  showSimpleLoading() {
    const cartDrawer = document.getElementById('CartDrawer');
    if (!cartDrawer) return;

    // Remove any existing loading overlay
    const existingOverlay = cartDrawer.querySelector('.simple-cart-loading');
    if (existingOverlay) {
      existingOverlay.remove();
    }

    // Create simple loading overlay with spinner - FULLY OPAQUE to hide old content
    const overlay = document.createElement('div');
    overlay.className = 'simple-cart-loading';
    overlay.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(255, 255, 255, 0.5);
      backdrop-filter: blur(4px);
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      gap: 1rem;
    `;

    // Add CSS animation to document head if not already there
    if (!document.getElementById('cart-spinner-styles')) {
      const style = document.createElement('style');
      style.id = 'cart-spinner-styles';
      style.textContent = `
        @keyframes cart-spinner-rotate {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes cart-spinner-dash {
          0% { stroke-dasharray: 1, 200; stroke-dashoffset: 0; }
          50% { stroke-dasharray: 89, 200; stroke-dashoffset: -35px; }
          100% { stroke-dasharray: 89, 200; stroke-dashoffset: -124px; }
        }
        .cart-loading-spinner {
          width: 50px;
          height: 50px;
          animation: cart-spinner-rotate 2s linear infinite;
        }
        .cart-loading-spinner circle {
          stroke: #dc600e;
          stroke-linecap: round;
          animation: cart-spinner-dash 1.5s ease-in-out infinite;
        }
        .cart-loading-text {
          font-size: 1.4rem;
          font-weight: 500;
          color: #414141;
          letter-spacing: -0.02em;
        }
        @media (max-width: 600px) {
          .cart-loading-spinner {
            width: 40px;
            height: 40px;
          }
          .cart-loading-text {
            font-size: 1.2rem;
          }
        }
      `;
      document.head.appendChild(style);
    }

    overlay.innerHTML = `
      <svg class="cart-loading-spinner" viewBox="0 0 66 66">
        <circle stroke-width="6" cx="33" cy="33" r="30" fill="none"></circle>
      </svg>
    `;

    cartDrawer.appendChild(overlay);
    console.log('Simple loading overlay with spinner added - old content hidden');
  }

  hideSimpleLoading() {
    const cartDrawer = document.getElementById('CartDrawer');
    if (!cartDrawer) return;

    const overlay = cartDrawer.querySelector('.simple-cart-loading');
    if (overlay) {
      overlay.remove();
      console.log('Simple loading overlay removed');
    }
  }

  close() {
    this.classList.remove('active');
    // removeTrapFocus(this.activeElement); // Removed to prevent unwanted scrolling
    document.body.classList.remove('overflow-hidden');

    // Reset recommendations state on drawer close
    if (window.cartComplementaryRecs) {
      window.cartComplementaryRecs.reset();
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Prevent DOM recreation conflicts - CartUpdateManager handles updates efficiently
    console.log('CartDrawer.renderContents() disabled - using efficient AJAX updates');

    // Only trigger update if CartUpdateManager is not already handling it
    if (window.cartUpdateManager && !window.cartUpdateManager.isUpdating) {
      window.cartUpdateManager.queueUpdate('cart-drawer-render', { parsedState });
    }
    return;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  // Helper to reset recommendations after cart update
  unblockRecommendations() {
    // Reset the new complementary recommendations system
    if (window.cartComplementaryRecs) {
      window.cartComplementaryRecs.reset();
      console.log('🔓 Complementary recommendations reset - ready for updates');
    }
  }

  // Enhanced cart update handling for empty-to-non-empty transitions
  onCartUpdate(source = null) {
    console.log('CartDrawerItems.onCartUpdate() - updating cart drawer', source ? `(source: ${source})` : '');

    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) {
      console.error('Cart drawer not found');
      return;
    }

    // Store source for later use in event dispatching
    this._updateSource = source;

    // Reset recommendations state to allow fresh load after DOM update
    // But skip reset for gift-related updates to maintain state
    if (window.cartComplementaryRecs && source !== 'product-gift' && source !== 'gift-note') {
      window.cartComplementaryRecs.reset();
      console.log('🔄 Reset complementary recommendations for fresh load');
    } else if (source === 'product-gift' || source === 'gift-note') {
      console.log('⏭️ Skipping recommendations reset for gift-related update');
    }

    // Check current cart state
    const wasEmpty = cartDrawer.classList.contains('is-empty');

    // Ensure loading is visible (it might already be shown from cart.js)
    // This is idempotent - if already shown, it will just remain shown
    cartDrawer.showSimpleLoading();

    // Safety timeout to ensure loading is always hidden
    setTimeout(() => {
      console.log('Safety timeout: forcing loading to hide');
      cartDrawer.hideSimpleLoading();
    }, 15000); // 15 second safety timeout

    // Check if cart is becoming empty by fetching current cart state
    fetch('/cart.js')
      .then((response) => response.json())
      .then((cartData) => {
        const isNowEmpty = cartData.item_count === 0;

        // Force DOM refresh for empty-to-non-empty OR non-empty-to-empty transitions
        if (wasEmpty !== isNowEmpty) {
          console.log(
            `Cart state change detected: ${wasEmpty ? 'empty' : 'filled'} -> ${
              isNowEmpty ? 'empty' : 'filled'
            } - using DOM refresh approach`
          );
          this.forceCartDrawerDOMRefresh(cartDrawer);
        } else {
          // For normal updates, use standard retry logic
          this.updateCartWithRetry(cartDrawer, 0, 3);
        }
      })
      .catch((error) => {
        console.error('Failed to check cart state:', error);
        // Fallback to standard retry logic
        this.updateCartWithRetry(cartDrawer, 0, 3);
      });
  }

  // Force complete DOM refresh for empty cart scenarios
  forceCartDrawerDOMRefresh(cartDrawer) {
    console.log('Forcing complete cart drawer DOM refresh');

    // First, verify cart state by checking cart.js
    fetch('/cart.js')
      .then((response) => response.json())
      .then((cartData) => {
        console.log('Cart.js verification:', cartData.item_count, 'items');

        // Proceed with DOM refresh regardless of cart state (empty or filled)
        // Multiple cache-busting parameters to ensure fresh content
        const cacheBust = `section_id=cart-drawer&timestamp=${Date.now()}&r=${Math.random()}&force=1`;
        const cartUrl = `${window.routes?.cart_url || '/cart'}?${cacheBust}`;

        return fetch(cartUrl, {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            Pragma: 'no-cache',
            Expires: '0',
          },
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            return response.text();
          })
          .then((responseText) => {
            console.log('DOM refresh response received, length:', responseText.length);

            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const newDrawerInner = html.querySelector('#CartDrawer .drawer__inner');
            const currentDrawerInner = document.querySelector('#CartDrawer .drawer__inner');

            if (!newDrawerInner) {
              throw new Error('New drawer inner content not found in response');
            }

            if (!currentDrawerInner) {
              throw new Error('Current drawer inner content not found');
            }

            // Verify we have cart items in the new content
            const cartItems = newDrawerInner.querySelectorAll('.cart-card, .cart-item');
            console.log(`DOM refresh found ${cartItems.length} cart items in HTML`);

            // Check if cart should no longer be empty
            const newCartDrawer = html.querySelector('cart-drawer');
            const shouldBeEmpty = newCartDrawer ? newCartDrawer.classList.contains('is-empty') : true;

            console.log(
              `Cart state check: shouldBeEmpty=${shouldBeEmpty}, cartItems=${cartItems.length}, actualCount=${cartData.item_count}`
            );

            // Only retry if cart should have items but none found in DOM
            // If cart should be empty (item_count === 0), proceed with update
            if (!shouldBeEmpty && cartItems.length === 0 && cartData.item_count > 0) {
              console.warn('Cart should have items but none found in DOM refresh, retrying...');
              // Fallback to retry mechanism
              setTimeout(() => {
                this.updateCartWithRetry(cartDrawer, 0, 5);
              }, 300);
              return;
            }

            // Store current state before DOM replacement
            const loadingOverlay = document.querySelector('#CartDrawer .simple-cart-loading');
            const scrollContainer = currentDrawerInner.querySelector('.cart-drawer__scrollable');
            const currentScrollTop = scrollContainer ? scrollContainer.scrollTop : 0;

            console.log('Performing drawer__inner content replacement...');

            // Replace only the drawer__inner content to preserve cart-drawer wrapper
            currentDrawerInner.innerHTML = newDrawerInner.innerHTML;

            // Update cart drawer state FIRST before custom elements initialize
            const currentCartDrawer = document.querySelector('cart-drawer');
            if (shouldBeEmpty) {
              currentCartDrawer.classList.add('is-empty');
            } else {
              currentCartDrawer.classList.remove('is-empty');
              console.log('Cart drawer is-empty class removed');
            }

            // Force custom element upgrade for cart-drawer-items
            const cartDrawerItems = document.querySelector('cart-drawer-items');
            if (cartDrawerItems && window.customElements) {
              console.log('Upgrading cart-drawer-items custom element');
              window.customElements.upgrade(cartDrawerItems);
            }

            // Verify cart items are now in the DOM
            const verifyCartItems = document.querySelectorAll('#CartDrawer .cart-card, #CartDrawer .cart-item');
            console.log(`Verification: ${verifyCartItems.length} cart items now in DOM`);

            // Restore loading overlay if it existed
            if (loadingOverlay) {
              const cartDrawerElement = document.querySelector('#CartDrawer');
              if (cartDrawerElement) {
                cartDrawerElement.appendChild(loadingOverlay);
              }
            }

            // Restore scroll position
            requestAnimationFrame(() => {
              const newScrollContainer = document.querySelector('#CartDrawer .cart-drawer__scrollable');
              if (newScrollContainer && currentScrollTop > 0) {
                newScrollContainer.scrollTop = currentScrollTop;
              }
            });

            // Force component reinitialization after DOM replacement
            setTimeout(() => {
              this.reinitializeCartDrawerComponents();
            }, 50);

            // Hide loading ONLY after DOM is fully updated and visible
            // Use requestAnimationFrame to ensure DOM has painted
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                if (cartDrawer) {
                  cartDrawer.hideSimpleLoading();
                  console.log('Loading hidden after DOM paint');
                }
              });
            });

            console.log('Cart drawer DOM refresh completed successfully');

            // Update cart icon immediately after DOM refresh
            this.updateCartIcon();

            // Unblock recommendations after DOM refresh
            this.unblockRecommendations();

            // Fetch fresh cart data and dispatch event with it
            fetch('/cart.js')
              .then((response) => response.json())
              .then((freshCartData) => {
                // Dispatch custom event with fresh cart data
                document.dispatchEvent(
                  new CustomEvent('cart:drawer:refreshed', {
                    detail: {
                      wasEmpty: true,
                      itemCount: cartItems.length,
                      cartData: freshCartData, // Pass cart data to avoid race condition
                    },
                  })
                );
              })
              .catch((error) => {
                console.error('Failed to fetch cart data:', error);
                // Dispatch event anyway without cart data
                document.dispatchEvent(
                  new CustomEvent('cart:drawer:refreshed', {
                    detail: { wasEmpty: true, itemCount: cartItems.length },
                  })
                );
              });
          });
      })
      .catch((error) => {
        console.error('Cart drawer DOM refresh failed:', error);

        // Unblock recommendations even on error
        this.unblockRecommendations();

        // Fallback to standard retry mechanism
        console.log('Falling back to standard retry mechanism');
        this.updateCartWithRetry(cartDrawer, 0, 5);
      });
  }

  // Update cart icon after DOM refresh
  updateCartIcon() {
    fetch('/cart.js')
      .then((response) => response.json())
      .then((cartData) => {
        console.log('Updating cart icon with fresh data:', cartData.item_count);

        // Update cart icon bubble
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble) {
          const countElement = cartIconBubble.querySelector('.cart-count-bubble span');
          if (countElement) {
            countElement.textContent = cartData.item_count;
          }

          // Update visibility
          const bubble = cartIconBubble.querySelector('.cart-count-bubble');
          if (bubble) {
            if (cartData.item_count > 0) {
              bubble.style.display = '';
            } else {
              bubble.style.display = 'none';
            }
          }
        }

        // Update mobile cart icon if it exists
        const mobileCartIcon = document.getElementById('cart-icon-bubble-mobile');
        if (mobileCartIcon) {
          const mobileCountElement = mobileCartIcon.querySelector('.cart-count-bubble span');
          if (mobileCountElement) {
            mobileCountElement.textContent = cartData.item_count;
          }
        }
      })
      .catch((error) => {
        console.error('Failed to update cart icon:', error);
      });
  }

  updateCartWithRetry(cartDrawer, attempt, maxAttempts = 3) {
    const delay = attempt === 0 ? 200 : 800; // Faster first attempt, then 800ms intervals

    setTimeout(() => {
      // Force cache bust with timestamp and random parameter
      const cacheBust = `timestamp=${Date.now()}&r=${Math.random()}`;
      fetch(`${window.routes?.cart_url || '/cart'}?section_id=cart-drawer&${cacheBust}`)
        .then((response) => response.text())
        .then((responseText) => {
          console.log(`Cart drawer response received (attempt ${attempt + 1})`);

          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const newDrawerInner = html.querySelector('#CartDrawer .drawer__inner');
          const currentDrawerInner = document.querySelector('#CartDrawer .drawer__inner');

          if (!newDrawerInner || !currentDrawerInner) {
            throw new Error('Cart drawer inner content not found in response');
          }

          // Check cart state transition
          const currentCartDrawer = document.querySelector('cart-drawer');
          const wasEmpty = currentCartDrawer.classList.contains('is-empty');
          const newCartDrawer = html.querySelector('cart-drawer');
          const willBeEmpty = newCartDrawer ? newCartDrawer.classList.contains('is-empty') : true;

          console.log(`Cart state transition: ${wasEmpty} -> ${willBeEmpty}`);

          // Verify cart state matches actual cart data
          fetch('/cart.js')
            .then((response) => response.json())
            .then((cartData) => {
              const actuallyEmpty = cartData.item_count === 0;
              if (actuallyEmpty !== willBeEmpty) {
                console.warn(
                  `Cart state mismatch! Server HTML says ${willBeEmpty ? 'empty' : 'filled'}, but cart.js says ${
                    actuallyEmpty ? 'empty' : 'filled'
                  }. Cart has ${cartData.item_count} items.`
                );
              }
            })
            .catch((err) => console.error('Failed to verify cart state:', err));

          // Verify cart items in response
          const cartItems = newDrawerInner.querySelectorAll('.cart-card, .cart-item');
          console.log(`Found ${cartItems.length} cart items in response`);

          // For empty-to-non-empty transitions, ensure we have items before updating
          if (wasEmpty && !willBeEmpty && cartItems.length === 0) {
            console.log('Empty-to-non-empty transition but no items found, retrying...');

            if (attempt < maxAttempts - 1) {
              this.updateCartWithRetry(cartDrawer, attempt + 1, maxAttempts);
              return;
            } else {
              // Final attempt - check cart.js directly
              return fetch('/cart.js')
                .then((response) => response.json())
                .then((cartData) => {
                  if (cartData.item_count > 0) {
                    console.log('Cart has items but drawer update failed, forcing page reload');
                    setTimeout(() => window.location.reload(), 500);
                  }
                });
            }
          }

          // Store current scroll position
          const scrollContainer = currentDrawerInner.querySelector('.cart-drawer__scrollable');
          const currentScrollTop = scrollContainer ? scrollContainer.scrollTop : 0;

          // Store loading overlay before replacement
          const loadingOverlay = document.querySelector('#CartDrawer .simple-cart-loading');

          // ATOMIC UPDATE: Replace drawer__inner content at once
          currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
          console.log('Cart drawer inner content replaced successfully');

          // Update cart drawer state classes FIRST before custom elements initialize
          if (willBeEmpty) {
            currentCartDrawer.classList.add('is-empty');
            console.log('Cart drawer is-empty class added');
          } else {
            currentCartDrawer.classList.remove('is-empty');
            console.log('Cart drawer is-empty class removed');
          }

          // Force custom element upgrade for cart-drawer-items
          const cartDrawerItems = document.querySelector('cart-drawer-items');
          if (cartDrawerItems && window.customElements) {
            console.log('Upgrading cart-drawer-items custom element');
            window.customElements.upgrade(cartDrawerItems);
          }

          // Restore scroll position immediately
          requestAnimationFrame(() => {
            const newScrollContainer = document.querySelector('#CartDrawer .cart-drawer__scrollable');
            if (newScrollContainer && currentScrollTop > 0) {
              newScrollContainer.scrollTop = currentScrollTop;
            }
          });

          // Restore loading overlay temporarily if it existed
          if (loadingOverlay) {
            const cartDrawerElement = document.querySelector('#CartDrawer');
            if (cartDrawerElement) {
              cartDrawerElement.appendChild(loadingOverlay);
            }
          }

          // Reinitialize components after DOM update
          this.reinitializeCartDrawerComponents();

          // Hide loading ONLY after DOM is fully updated and visible
          // Use requestAnimationFrame to ensure DOM has painted
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (cartDrawer) {
                cartDrawer.hideSimpleLoading();
                console.log('Loading hidden after DOM paint');
              }
            });
          });

          console.log('Cart drawer update completed successfully');

          // Unblock recommendations after update
          this.unblockRecommendations();

          // Fetch cart data once and dispatch event
          // Use a single fetch to avoid multiple calls
          fetch('/cart.js')
            .then((response) => response.json())
            .then((cartData) => {
              console.log('✅ Cart data fetched, dispatching cart:drawer:refreshed event');
              document.dispatchEvent(
                new CustomEvent('cart:drawer:refreshed', {
                  detail: {
                    wasEmpty: false,
                    itemCount: cartData.item_count,
                    cartData: cartData,
                  },
                })
              );
            })
            .catch((error) => {
              console.error('Failed to fetch cart data for recommendations:', error);
              // Dispatch without cart data as fallback
              document.dispatchEvent(
                new CustomEvent('cart:drawer:refreshed', {
                  detail: { wasEmpty: false, itemCount: cartItems.length },
                })
              );
            });
        })
        .catch((error) => {
          console.error(`Failed to update cart drawer (attempt ${attempt + 1}):`, error);

          // Retry if we haven't reached max attempts
          if (attempt < maxAttempts - 1) {
            console.log(`Retrying cart update (attempt ${attempt + 2})`);
            this.updateCartWithRetry(cartDrawer, attempt + 1, maxAttempts);
          } else {
            console.log('All cart update attempts failed, hiding loading');
            setTimeout(() => {
              if (cartDrawer) {
                cartDrawer.hideSimpleLoading();
              }
              this.unblockRecommendations();
            }, 300);
          }
        });
    }, delay);
  }

  reinitializeCartDrawerComponents() {
    // Reinitialize quantity controls
    const quantityInputs = document.querySelectorAll('#CartDrawer .cart-drawer-quantity');
    quantityInputs.forEach((input) => {
      const wrapper = input.closest('.cart-drawer-quantity-wrapper');
      if (wrapper && this.initQuantityControls) {
        this.initQuantityControls(wrapper);
      }
    });

    // Reinitialize other components
    if (this.initExpandCollapse) this.initExpandCollapse();
    if (this.initMiniCollectionNavigation) this.initMiniCollectionNavigation();

    // Reinitialize cart footer toggle - call global function
    if (window.initializeCartFooterToggle) {
      window.initializeCartFooterToggle();
    }

    if (this.initMiniCollectionSpacing) this.initMiniCollectionSpacing();

    // Reinitialize price stages progress bar
    if (window.initializeCartPriceStages) {
      console.log('Reinitializing cart price stages after DOM update');
      setTimeout(() => {
        window.initializeCartPriceStages();
      }, 100);
    }

    // Reinitialize complementary recommendations quick-add buttons
    if (window.cartComplementaryRecs) {
      console.log('Reinitializing complementary recommendations buttons');
      const miniCollectionContainer = document.querySelector('.cart-drawer__mini-collection-products');
      if (miniCollectionContainer) {
        window.cartComplementaryRecs.initializeQuickAdd(miniCollectionContainer);
      }
    }

    // Reinitialize product reward handler to ensure events work after DOM updates
    if (window.cartProductRewardHandlerInstance) {
      console.log('Ensuring product reward handler events are attached after DOM update');
      window.cartProductRewardHandlerInstance.ensureEventsAttached();
    }

    // Format prices
    setTimeout(() => {
      const cartDrawer = document.querySelector('#CartDrawer');
      if (cartDrawer) {
        const subtotalElements = cartDrawer.querySelectorAll(
          '.cart-footer__subtotal-value, .cart-footer__breakdown-value'
        );
        subtotalElements.forEach((element) => {
          if (element.textContent && !element.dataset.formatted) {
            element.textContent = window.formatIndianPrice
              ? window.formatIndianPrice(element.textContent)
              : element.textContent;
            element.dataset.formatted = 'true';
          }
        });
      }
    }, 100);
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Global event listener for cart drawer refresh events
document.addEventListener('cart:drawer:refreshed', (event) => {
  console.log('Cart drawer refresh event received:', event.detail);

  // Ensure all price formatting is applied
  setTimeout(() => {
    const cartDrawer = document.querySelector('#CartDrawer');
    if (cartDrawer && window.formatIndianPrice) {
      const priceElements = cartDrawer.querySelectorAll(
        '.cart-footer__subtotal-value, .cart-footer__breakdown-value, .price'
      );
      priceElements.forEach((element) => {
        if (element.textContent && !element.dataset.formatted) {
          element.textContent = window.formatIndianPrice(element.textContent);
          element.dataset.formatted = 'true';
        }
      });
    }
  }, 100);
});
